import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, TrendingUp, DollarSign, BarChart3, Target, Clock, Award, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { fmtUSD } from "../utils/currency";
const fmt$ = (v) => fmtUSD(v);
const LOAN_LABELS = {
  fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi",
  construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial",
};

export default function PortfolioAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("ic_token");

  useEffect(() => {
    axios.get(`${API}/analytics/portfolio`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  if (!data || data.total_deals === 0) {
    return (
      <div className="text-center py-16 border-2 border-dashed border-[#e8e0d0] rounded-2xl" data-testid="empty-analytics">
        <BarChart3 className="h-10 w-10 mx-auto mb-3 text-[#ccc]" />
        <h3 className="text-sm font-bold text-[#555]">No analytics yet</h3>
        <p className="text-xs text-[#888] mt-1">Submit your first capital request to see portfolio analytics.</p>
      </div>
    );
  }

  const metrics = [
    { label: "Total Deals", value: data.total_deals, icon: BarChart3, color: "emerald" },
    { label: "Funded", value: data.funded_count, icon: Award, color: "blue" },
    { label: "Active", value: data.active_count, icon: TrendingUp, color: "amber" },
    { label: "Success Rate", value: `${data.success_rate}%`, icon: Target, color: "emerald" },
    { label: "Total Volume", value: fmt$(data.total_volume), icon: DollarSign, color: "blue" },
    { label: "Avg Loan", value: fmt$(data.avg_loan_amount), icon: DollarSign, color: "purple" },
    { label: "Avg LTC", value: `${data.avg_ltc}%`, icon: TrendingUp, color: data.avg_ltc > 85 ? "amber" : "emerald" },
    { label: "Avg LTV", value: `${data.avg_ltv}%`, icon: TrendingUp, color: data.avg_ltv > 75 ? "amber" : "emerald" },
    { label: "Avg Days to Fund", value: data.avg_days_to_fund ? `${data.avg_days_to_fund}d` : "--", icon: Clock, color: "blue" },
  ];

  return (
    <div className="space-y-6" data-testid="portfolio-analytics">
      <div>
        <h2 className="text-base font-bold text-[#0A0A0A]">Portfolio Analytics</h2>
        <p className="text-xs text-[#888] mt-0.5">Aggregate performance across all your deals.</p>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="portfolio-kpis">
        {metrics.map((m, i) => (
          <div key={i} className="rounded-xl border border-[#e8e0d0] bg-white p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <m.icon className={`h-3.5 w-3.5 text-${m.color}-600`} />
              <span className="text-[9px] text-[#999] uppercase font-bold">{m.label}</span>
            </div>
            <div className="text-lg font-black text-[#222]">{m.value}</div>
          </div>
        ))}
      </div>

      {/* Breakdowns */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Loan Type Breakdown */}
        {data.loan_type_breakdown?.length > 0 && (
          <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid="loan-type-breakdown">
            <h3 className="text-xs font-bold text-[#555] mb-3">By Loan Program</h3>
            <div className="space-y-2">
              {data.loan_type_breakdown.map((lt, i) => {
                const pct = Math.round((lt.count / data.total_deals) * 100);
                return (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-[10px] font-medium text-[#666] w-24 truncate">{LOAN_LABELS[lt.type] || lt.type}</span>
                    <div className="flex-1 h-2 rounded-full bg-[#f0ebe0] overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[10px] font-bold text-[#555] w-12 text-right">{lt.count} ({pct}%)</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Stage Distribution */}
        {data.stage_distribution?.length > 0 && (
          <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid="stage-distribution">
            <h3 className="text-xs font-bold text-[#555] mb-3">Current Pipeline Stage</h3>
            <div className="space-y-2">
              {data.stage_distribution.map((sg, i) => {
                const pct = Math.round((sg.count / data.total_deals) * 100);
                return (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-[10px] font-medium text-[#666] w-24 truncate capitalize">{sg.stage.replace(/_/g, " ")}</span>
                    <div className="flex-1 h-2 rounded-full bg-[#f0ebe0] overflow-hidden">
                      <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[10px] font-bold text-[#555] w-12 text-right">{sg.count} ({pct}%)</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* State Breakdown */}
        {data.state_breakdown?.length > 0 && (
          <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid="state-breakdown">
            <h3 className="text-xs font-bold text-[#555] mb-3">By State</h3>
            <div className="flex flex-wrap gap-2">
              {data.state_breakdown.map((st, i) => (
                <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#faf7f2] border border-[#e8e0d0]">
                  <MapPin className="h-3 w-3 text-[#888]" />
                  <span className="text-xs font-semibold text-[#333]">{st.state || "N/A"}</span>
                  <span className="text-[9px] text-[#999]">({st.count})</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
